import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import CheckboxToggle from 'sg/CheckboxToggle/CheckboxToggle.react';
import createAssignmentStore from '../../CreateAssignment.store';
import track from 'react-tracking';
import {Text} from '@albert-io/atomic';

@track()
class CreateAssignmentRecipientItem extends React.Component {
  static propTypes = {
    autoAssignNewEnrollees: PropTypes.bool,
    checked: PropTypes.bool.isRequired,
    children: PropTypes.object,
    disabled: PropTypes.bool,
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    onAutoAssignNewEnrolleesToggle: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    toggleDisabled: PropTypes.bool,
    tracking: PropTypes.object,
    value: PropTypes.any.isRequired,
    size: PropTypes.number,
    nested: PropTypes.bool
  };

  static defaultProps = {
    disabled: false,
    size: null,
    nested: false
  };

  constructor(props) {
    super(props);

    this.accordionNodes = {
      trigger: {},
      drawer: {}
    };
  }

  handleAccordionClick = () => {
    const itemGroup = this.accordionNodes.drawer;
    const {trigger} = this.accordionNodes;
    this.props.tracking.trackEvent({
      type: 'button',
      content: 'View/hide individual students',
      event: 'click'
    });
    if (itemGroup.style.maxHeight) {
      itemGroup.style.maxHeight = null;
      trigger.style.transform = null;
      trigger.setAttribute('aria-expanded', false);
    } else {
      itemGroup.style.maxHeight = `${itemGroup.scrollHeight}px`;
      trigger.style.transform = 'rotate(90deg)';
      trigger.setAttribute('aria-expanded', 'true');
    }
  };

  handleAccordionKeyPress = (e) => {
    if (e.which === 13) {
      // Enter key
      this.handleAccordionClick();
    }
  };

  render() {
    /* These recipient list items are generated from an iterable, and the `size`
     * prop tells us whether or not a sub-iterable is generating sub-items.
     */
    const recipients = createAssignmentStore.getSelectedRecipients();
    const recipientCount = recipients.get(this.props.id)
      ? recipients.get(this.props.id).first().size
      : 0;
    const subItemsTotal = this.props.size;
    const isSubItem = subItemsTotal === null;
    const isDisabled = this.props.disabled;
    const hasSubItems = !isSubItem && subItemsTotal >= 0;
    const itemClassNames = classnames('acf__recipient-item', {
      'acf__recipient-item--nested': isSubItem,
      'acf__recipient-item--disabled': isDisabled
    });

    const allSelected = recipientCount === subItemsTotal;

    const indeterminate = recipientCount > 0 && !allSelected;

    const autoAssignNewEnrolleesToggle = hasSubItems ? (
      <CheckboxToggle
        className='acf__assign-new-enrollees-toggle'
        checked={this.props.autoAssignNewEnrollees}
        disabled={this.props.toggleDisabled}
        label='Assign to future enrollees'
        onChange={this.props.onAutoAssignNewEnrolleesToggle}
      />
    ) : null;

    const accordionLabel = allSelected
      ? `(All ${recipientCount} selected)`
      : ` (${recipientCount} selected)`;

    const accordionTrigger = hasSubItems ? (
      <div
        className='u-display_flex acf__accordion-wrapper a-text--color-black-lowest'
        onClick={this.handleAccordionClick}
        aria-expanded='false'
        aria-label={`Show/hide roster for ${this.props.name}`}
        aria-labelledby={`labelFor_${this.props.id}`}
        role='button'
        tabIndex='0'
        onKeyPress={this.handleAccordionKeyPress}
      >
        <div
          className='acf__accordion-trigger u-mar-t_1'
          ref={(node) => {
            this.accordionNodes.trigger = node;
          }}
          role='presentation'
        />
        <Text emphasis='lowest' color='inherit' size='m' className='afc__accordion-label'>
          Select students {!(recipientCount === 0) && accordionLabel}
        </Text>
      </div>
    ) : null;

    const accordionTabPanel = (
      <div
        aria-labelledby={`labelFor_${this.props.id}`}
        className='acf__recipient-item-children'
        ref={(node) => {
          this.accordionNodes.drawer = node;
        }}
        role='tabpanel'
      >
        {subItemsTotal > 0 ? (
          this.props.children
        ) : (
          <span className='acf__recipient-item-children-empty-message'>
            This classroom has no students yet.
          </span>
        )}
      </div>
    );
    return (
      <div className='acf__recipient-item-wrapper'>
        <div className={itemClassNames}>
          <label className='acf__recipient-item-checkbox-wrapper' htmlFor={this.props.id}>
            <input
              checked={this.props.checked}
              disabled={this.props.disabled}
              id={this.props.id}
              onChange={this.props.onChange}
              onKeyPress={(e) => {
                if (e.which === 13) {
                  this.props.onChange(e);
                }
              }}
              type='checkbox'
              value={this.props.value}
            />
            {/* eslint-disable jsx-a11y/control-has-associated-label */}
            <span
              aria-checked={this.props.checked}
              className={classnames('checkbox-checkmark', 'acf__recipient-item-checkbox', {
                'acf__recipient-item-checkbox-indeterminate': indeterminate
              })}
              role='checkbox'
            />
          </label>
          <div className='u-display_flex u-flex-direction_column'>
            <label className='acf__recipient-item-label' htmlFor={this.props.id}>
              <Text
                bold
                emphasis={this.props.nested ? 'high' : 'highest'}
                size={this.props.nested ? 's' : 'm'}
                id={`labelFor_${this.props.id}`} // Linked with aria-labelledby
              >
                {this.props.name}
              </Text>
            </label>
            {accordionTrigger}
          </div>

          {autoAssignNewEnrolleesToggle}
        </div>
        {hasSubItems ? accordionTabPanel : null}
      </div>
    );
  }
}

export default CreateAssignmentRecipientItem;
